import { observable, runInAction } from 'mobx';
import type { AnyLocaleType } from '@featit/shared/i18n/core';
import type { TFn } from './LocaleProvider';
import { setWorkAreaSeedTranslator } from '@featit/shared/work-area-seed-names';

/**
 * I-1925: the open page's translator, for the one kind of text a plain function builds rather than a
 * component: a refused control's reason, which `workAreaGate` and the other gates compose and
 * `gateControlProps` sets as the control's `title` at every one of its call sites. `LocaleProvider`
 * sets it as it renders, so a gate built below it in the same render reads the page's language.
 *
 * ⛔ Only for text built outside a component. A component asks `useT()` (ADR-148); this is not a way
 * round it. Without a provider (a unit test), English.
 */
let current: TFn | null = null;
/** T-0866: the language `current` speaks, for a number a plain function formats ({@link pageLocale}). */
let currentLocale: AnyLocaleType | null = null;
/**
 * T-0471: counts the page's languages so far. A store's getter that words a kept failure with
 * {@link pageT} reads it, so the sentence a screen shows is written again when the language changes.
 */
const language = observable.box(0);

export function setPageTranslator(t: TFn, locale: AnyLocaleType | null = null): void {
  current = t;
  currentLocale = locale;
}

/**
 * I-1976: a provider that leaves the page takes its translator with it, so a failure built after it
 * has gone — or in the next test — is not drawn in a language no provider speaks any more.
 */
export function clearPageTranslator(t: TFn): void {
  if (current !== t) return;
  current = null;
  currentLocale = null;
}

/** The page's language has changed: every getter that worded text with {@link pageT} words it again. */
export function announcePageLanguage(): void {
  runInAction(() => language.set(language.get() + 1));
}

/** The page's translator, or `fallback` (English) before a provider has rendered. */
export function pageT(fallback: TFn): TFn {
  language.get();
  return current ?? fallback;
}

/**
 * T-0866: the page's language, or `fallback` (English) before a provider has rendered: for the numbers
 * a plain function writes into a {@link pageT} sentence, through `formatNumber` as `useFormat` does.
 */
export function pageLocale(fallback: AnyLocaleType): AnyLocaleType {
  language.get();
  return current ? (currentLocale ?? fallback) : fallback;
}

/**
 * F-0737 PW-R3: a Personal work area's name is the product's until its person renames it, so every
 * label built from `workAreaDisplayName` (`workAreaQualifiedLabel`, the sidebar, the pickers) says it
 * in the open page's language. It reads {@link language}, so an observer draws it again on a switch;
 * without a provider it answers the key, and the stored English name is drawn.
 */
setWorkAreaSeedTranslator((key) => {
  language.get();
  return current ? current(key) : key;
});
